import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {
    coercedIntegerZodValidator,
    iso8601DateTimeZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import type {TuffexProductInventory} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductInventoryWorkspaceCapability/domain/TuffexProductInventory";
import {
    type ProductInventoryItemLog,
    ProductInventoryItemSource,
    productInventoryEnrichedValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductInventoryWorkspaceCapability/domain/TuffexProductInventoryEnrichment";
import {verifyTuffexProductCatalogueRowExists} from "~/domainModels/ottokart/tuffexProductCatalogue";
import {
    resolveOrganizationalUnitIdForWarehouse,
} from "~/domainModels/ottokart/organizationalUnits";
import {plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import type {ZodType} from "zod";
import {z as zod} from "zod";

const PRODUCT_INVENTORY_PAGE_SIZE = 500;

function normalizeIso8601DateTime(value: unknown): unknown {
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value !== "string") {
        return value;
    }
    const trimmed = value.trim().replace(" ", "T");
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime()) === true) {
        return trimmed.replace(/\+00:00$/, "Z").replace(/\+00$/, "Z");
    }
    return parsed.toISOString();
}

const productInventoryResponseValidator: ZodType<TuffexProductInventory> = zod.object({
    createdAt: zod.preprocess(normalizeIso8601DateTime, iso8601DateTimeZodValidator),
    createdBy: uuidZodValidator,
    id: uuidZodValidator,
    inventory: coercedIntegerZodValidator,
    ouId: uuidZodValidator,
    productCatalogueId: uuidZodValidator,
    updatedAt: zod.preprocess(normalizeIso8601DateTime, iso8601DateTimeZodValidator),
    updatedBy: uuidZodValidator,
    workspaceId: uuidZodValidator,
});

function parseInventoryItemSource(value: unknown): ProductInventoryItemSource {
    if (value === ProductInventoryItemSource.throughProducts || value === "THROUGH_PRODUCTS") {
        return ProductInventoryItemSource.throughProducts;
    }
    if (value === ProductInventoryItemSource.throughCsvSync || value === "THROUGH_CSV_SYNC") {
        return ProductInventoryItemSource.throughCsvSync;
    }
    return ProductInventoryItemSource.throughApi;
}

const inventoryItemLogResponseValidator: ZodType<ProductInventoryItemLog> = zod.object({
    id: uuidZodValidator,
    productCatalogueId: uuidZodValidator,
    sku: zod.string(),
    plantId: zod.string().nullable(),
    availableInventory: coercedIntegerZodValidator,
    storageLocation: zod.string().nullable(),
    createdAt: zod.preprocess(normalizeIso8601DateTime, iso8601DateTimeZodValidator),
    updatedAt: zod.preprocess(normalizeIso8601DateTime, iso8601DateTimeZodValidator),
    source: zod.unknown().transform((value) => {
        return parseInventoryItemSource(value);
    }),
});

export type Inventory = {
    id: Uuid;
    variantId: Uuid;
    sku: string;
    inventory: number;
    ouId: Uuid;
    plantId: string | null;
    storageLocation: string | null;
    categoryName: string | null;
    brandName: string | null;
    typeName: string | null;
    manufacturerName: string | null;
    lastInventoryItemUpdatedAt: Iso8601DateTime | null;
    updatedAt: Iso8601DateTime;
};

export type InventoryType = {
    sku: string;
    availableInventory: number;
    plantId?: string | null;
    storageLocation?: string | null;
};

export const inventoryZodValidator: ZodType<Inventory> = zod.object({
    id: uuidZodValidator,
    variantId: uuidZodValidator,
    sku: zod.string(),
    inventory: zod.number().int(),
    ouId: uuidZodValidator,
    plantId: zod.string().nullable(),
    storageLocation: zod.string().nullable(),
    categoryName: zod.string().nullable(),
    brandName: zod.string().nullable(),
    typeName: zod.string().nullable(),
    manufacturerName: zod.string().nullable(),
    lastInventoryItemUpdatedAt: iso8601DateTimeZodValidator.nullable(),
    updatedAt: iso8601DateTimeZodValidator,
});

async function fetchProductInventoriesPage({
    accessToken,
    limit,
    offset,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    limit: number;
    offset: number;
}): Promise<Result<Array<TuffexProductInventory>>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/tuffex-product-inventories/get",
        body: {
            limit: limit,
            offset: offset,
        },
        responseValidator: zod.array(productInventoryResponseValidator),
    });
}

export async function fetchAllProductInventories(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Array<TuffexProductInventory>>> {
    const allRows: Array<TuffexProductInventory> = [];
    let offset = 0;

    while (true) {
        const pageResult = await fetchProductInventoriesPage({
            ...context,
            limit: PRODUCT_INVENTORY_PAGE_SIZE,
            offset: offset,
        });
        if (pageResult.success !== true) {
            return pageResult;
        }

        allRows.push(...pageResult.data);

        if (pageResult.data.length < PRODUCT_INVENTORY_PAGE_SIZE) {
            break;
        }

        offset += PRODUCT_INVENTORY_PAGE_SIZE;
    }

    return dataResult(allRows);
}

export {resolveDefaultOrganizationalUnitId} from "~/domainModels/ottokart/organizationalUnits";

export function buildProductCatalogueIdToInventoryCountMap(
    productInventories: Array<TuffexProductInventory>,
): Map<Uuid, number> {
    const inventoryCountByProductCatalogueId = new Map<Uuid, number>();

    for (const productInventory of productInventories) {
        const existingCount = inventoryCountByProductCatalogueId.get(productInventory.productCatalogueId) ?? 0;
        inventoryCountByProductCatalogueId.set(
            productInventory.productCatalogueId,
            existingCount + productInventory.inventory,
        );
    }

    return inventoryCountByProductCatalogueId;
}

async function findProductInventoryForCatalogueAndOu({
    accessToken,
    ouId,
    intellsysBaseUrl,
    productCatalogueId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    ouId: Uuid;
    productCatalogueId: Uuid;
}): Promise<Result<TuffexProductInventory | null>> {
    const getResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/tuffex-product-inventories/get",
        body: {
            filter: {
                productCatalogueId: productCatalogueId,
                ouId: ouId,
            },
            limit: 1,
            offset: 0,
        },
        responseValidator: zod.array(productInventoryResponseValidator),
    });
    if (getResult.success !== true) {
        return getResult;
    }

    const firstRow = getResult.data[0];
    if (firstRow === undefined) {
        return dataResult(null);
    }

    return dataResult(firstRow);
}

export async function upsertProductCatalogueInventory({
    accessToken,
    inventory,
    ouId,
    plantId,
    intellsysBaseUrl,
    productCatalogueId,
    source,
    storageLocation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    inventory: number;
    ouId?: Uuid;
    plantId?: string | null;
    productCatalogueId: Uuid;
    source?: ProductInventoryItemSource;
    storageLocation?: string | null;
}): Promise<Result<void>> {
    const productCatalogueVerifyResult = await verifyTuffexProductCatalogueRowExists({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueId: productCatalogueId,
        workspaceId: workspaceId,
    });
    if (productCatalogueVerifyResult.success !== true) {
        return productCatalogueVerifyResult;
    }

    const resolvedOuIdResult = ouId !== undefined
        ? dataResult(ouId)
        : await resolveOrganizationalUnitIdForWarehouse({
            accessToken: accessToken,
            plantId: plantId,
            intellsysBaseUrl: intellsysBaseUrl,
            storageLocation: storageLocation,
            workspaceId: workspaceId,
        });
    if (resolvedOuIdResult.success !== true) {
        return resolvedOuIdResult;
    }

    const existingInventoryResult = await findProductInventoryForCatalogueAndOu({
        accessToken: accessToken,
        ouId: resolvedOuIdResult.data,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueId: productCatalogueId,
        workspaceId: workspaceId,
    });
    if (existingInventoryResult.success !== true) {
        return existingInventoryResult;
    }

    if (existingInventoryResult.data === null) {
        const createResult = await plutoPost({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            path: "/tuffex-product-inventories/create",
            body: {
                tuffexProductInventory: {
                    productCatalogueId: productCatalogueId,
                    ouId: resolvedOuIdResult.data,
                    inventory: inventory,
                },
                ...(source !== undefined ? {source: source} : {}),
            },
            responseValidator: zod.unknown(),
        });
        if (createResult.success !== true) {
            return errorResult(createResult.error);
        }
        return dataResult(undefined);
    }

    const updateResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/tuffex-product-inventories/update",
        body: {
            tuffexProductInventory: {
                id: existingInventoryResult.data.id,
                inventory: inventory,
            },
            ...(source !== undefined ? {source: source} : {}),
        },
        responseValidator: zod.unknown(),
    });
    if (updateResult.success !== true) {
        return errorResult(updateResult.error);
    }

    return dataResult(undefined);
}

export type InventoryListServerFilterColumn =
    | "brandName"
    | "categoryName"
    | "plantId"
    | "storageLocation"
    | "typeName"
    | "updatedAt";

export type InventoryListServerFilters = {
    brandNames?: Array<string>;
    categoryNames?: Array<string>;
    plantIds?: Array<string>;
    storageLocations?: Array<string>;
    typeNames?: Array<string>;
    updatedAtFrom?: string | null;
    updatedAtTo?: string | null;
};

function inventoryFiltersBody(
    filters: InventoryListServerFilters | undefined,
): {filters: InventoryListServerFilters} | Record<string, never> {
    if (filters === undefined) {
        return {};
    }
    const requestFilters: InventoryListServerFilters = {};
    if (filters.brandNames !== undefined) {
        requestFilters.brandNames = filters.brandNames;
    }
    if (filters.categoryNames !== undefined) {
        requestFilters.categoryNames = filters.categoryNames;
    }
    if (filters.plantIds !== undefined) {
        requestFilters.plantIds = filters.plantIds;
    }
    if (filters.storageLocations !== undefined) {
        requestFilters.storageLocations = filters.storageLocations;
    }
    if (filters.typeNames !== undefined) {
        requestFilters.typeNames = filters.typeNames;
    }
    if (filters.updatedAtFrom !== undefined && filters.updatedAtTo !== undefined) {
        requestFilters.updatedAtFrom = filters.updatedAtFrom;
        requestFilters.updatedAtTo = filters.updatedAtTo;
    }
    if (
        requestFilters.brandNames === undefined &&
        requestFilters.categoryNames === undefined &&
        requestFilters.plantIds === undefined &&
        requestFilters.storageLocations === undefined &&
        requestFilters.typeNames === undefined &&
        requestFilters.updatedAtFrom === undefined &&
        requestFilters.updatedAtTo === undefined
    ) {
        return {};
    }
    return {filters: requestFilters};
}

function mapEnrichedInventoryRow(row: zod.infer<typeof productInventoryEnrichedValidator>): Inventory {
    return {
        id: row.id,
        variantId: row.productCatalogueId,
        sku: row.sku,
        inventory: row.inventory,
        ouId: row.ouId,
        plantId: row.plantId,
        storageLocation: row.storageLocation,
        categoryName: row.categoryName,
        brandName: row.brandName,
        typeName: row.typeName,
        manufacturerName: row.manufacturerName,
        lastInventoryItemUpdatedAt: row.lastInventoryItemUpdatedAt,
        updatedAt: row.updatedAt,
    };
}

export async function listInventoryColumnValues({
    accessToken,
    column,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    column: Exclude<InventoryListServerFilterColumn, "updatedAt">;
}): Promise<Result<Array<string>>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/tuffex-product-inventories/list-enriched",
        body: {
            columnValuesFor: column,
        },
        responseValidator: zod.array(zod.string()),
    });
}

export async function listInventory({
    accessToken,
    filters,
    intellsysBaseUrl,
    offset,
    pageSize = PRODUCT_INVENTORY_PAGE_SIZE,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: InventoryListServerFilters;
    offset?: number;
    pageSize?: number;
    search?: string;
}): Promise<Result<Array<Inventory>>> {
    const trimmedSearch = search?.trim() ?? "";
    const searchBody = trimmedSearch.length > 0 ? {search: trimmedSearch} : {};
    const filterBody = inventoryFiltersBody(filters);
    if (offset !== undefined) {
        const pageResult = await plutoPost({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            path: "/tuffex-product-inventories/list-enriched",
            body: {
                limit: Math.max(1, pageSize),
                offset: Math.max(0, offset),
                ...searchBody,
                ...filterBody,
            },
            responseValidator: zod.array(productInventoryEnrichedValidator),
        });
        if (pageResult.success !== true) {
            return pageResult;
        }
        return dataResult(pageResult.data.map(mapEnrichedInventoryRow));
    }

    const safePageSize = Math.max(1, pageSize);
    const allRows: Array<Inventory> = [];
    let pageOffset = 0;

    while (true) {
        const pageResult = await plutoPost({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            path: "/tuffex-product-inventories/list-enriched",
            body: {
                limit: safePageSize,
                offset: pageOffset,
                ...searchBody,
                ...filterBody,
            },
            responseValidator: zod.array(productInventoryEnrichedValidator),
        });
        if (pageResult.success !== true) {
            return pageResult;
        }

        for (const row of pageResult.data) {
            allRows.push(mapEnrichedInventoryRow(row));
        }

        if (pageResult.data.length < safePageSize) {
            break;
        }
        pageOffset += safePageSize;
    }

    return dataResult(allRows);
}

export async function countInventory({
    accessToken,
    filters,
    intellsysBaseUrl,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: InventoryListServerFilters;
    search?: string;
}): Promise<Result<number>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/tuffex-product-inventories/list-enriched",
        body: {
            countOnly: true,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...inventoryFiltersBody(filters),
        },
        responseValidator: zod.coerce.number().int().nonnegative(),
    });
}

export async function listInventoryItemsBySku({
    accessToken,
    intellsysBaseUrl,
    productCatalogueId,
    sku,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    productCatalogueId?: Uuid;
    sku: string;
}): Promise<Result<Array<ProductInventoryItemLog>>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/tuffex-product-inventories/list-items-by-sku",
        body: {
            productCatalogueId: productCatalogueId,
            sku: sku,
        },
        responseValidator: zod.array(inventoryItemLogResponseValidator),
    });
}

export function inventorySnapshotAsItemLog(inventoryRow: Inventory): ProductInventoryItemLog {
    return {
        id: inventoryRow.id,
        productCatalogueId: inventoryRow.variantId,
        sku: inventoryRow.sku,
        plantId: inventoryRow.plantId,
        availableInventory: inventoryRow.inventory,
        storageLocation: inventoryRow.storageLocation,
        createdAt: inventoryRow.updatedAt,
        updatedAt: inventoryRow.updatedAt,
        source: ProductInventoryItemSource.throughProducts,
    };
}

export async function bulkUpdateInventory({
    accessToken,
    items,
    ouId,
    intellsysBaseUrl,
    productCatalogueIdBySku,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    items: Array<InventoryType>;
    ouId?: Uuid;
    productCatalogueIdBySku: Map<string, Uuid>;
}): Promise<Result<string>> {
    for (const item of items) {
        const normalizedSku = item.sku.trim().toLowerCase();
        const productCatalogueId = productCatalogueIdBySku.get(normalizedSku);
        if (productCatalogueId === undefined) {
            return errorResult(new Error(`No product catalogue found for SKU: ${item.sku}`));
        }

        const resolvedOuIdResult = ouId !== undefined
            ? dataResult(ouId)
            : await resolveOrganizationalUnitIdForWarehouse({
                accessToken: accessToken,
                plantId: item.plantId,
                intellsysBaseUrl: intellsysBaseUrl,
                storageLocation: item.storageLocation,
                workspaceId: workspaceId,
            });
        if (resolvedOuIdResult.success !== true) {
            return resolvedOuIdResult;
        }

        const upsertResult = await upsertProductCatalogueInventory({
            accessToken: accessToken,
            inventory: item.availableInventory,
            ouId: resolvedOuIdResult.data,
            ...(item.plantId !== undefined &&
            item.plantId !== null &&
            item.plantId.trim().length > 0
                ? {plantId: item.plantId.trim()}
                : {}),
            intellsysBaseUrl: intellsysBaseUrl,
            productCatalogueId: productCatalogueId,
            source: ProductInventoryItemSource.throughCsvSync,
            ...(item.storageLocation !== undefined &&
            item.storageLocation !== null &&
            item.storageLocation.trim().length > 0
                ? {storageLocation: item.storageLocation.trim()}
                : {}),
            workspaceId: workspaceId,
        });
        if (upsertResult.success !== true) {
            return upsertResult;
        }
    }

    return dataResult("Inventory updated successfully");
}
